import React, { useState, useEffect } from 'react';
import { Box, IconButton, useTheme, Button } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Delete, EditOutlined } from "@mui/icons-material";
import { tokens } from '../../theme';
import { useNavigate } from "react-router-dom";
import useSessionTimeRangeService from '../../api/services/SessionTimeRangeService';
import Table from '../../components/Table';
import Header from "../../components/Header";
import LoadingSpinner from '../../components/LoadingSpinner';
import CreateDialog from "./Create_SessionTimeRange";
import EditSessionTimeRangeDialog from "./Edit_SessionTimeRange";
import DeleteDialog from "./Delete_SessionTimeRange";

const SessionTimeRanges = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { sessionTimeRanges, totalRecords, fetchSessionTimeRanges, updateSessionTimeRangeStatus } = useSessionTimeRangeService();

  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [showAllSessionTimeRanges, setShowAllSessionTimeRanges] = useState(false);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedIdForEdit, setSelectedIdForEdit] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSessionTimeRangeId, setSelectedSessionTimeRangeId] = useState(null);
  const [selectedSessionTimeRangeStatus, setSelectedSessionTimeRangeStatus] = useState(null);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadSessionTimeRanges = async () => {
    try {
      setLoading(true);
      await fetchSessionTimeRanges(page, size, showAllSessionTimeRanges ? null : "ACTIVE");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessionTimeRanges();
  }, [page, showAllSessionTimeRanges]);

  const handlePageChange = (newPage) => setPage(newPage);

  const handleEditClick = (id) => {
    setSelectedIdForEdit(id);
    setIsEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedIdForEdit(null);
    loadSessionTimeRanges();
  };

  const handleDeleteClick = (id, status) => {
    setSelectedSessionTimeRangeId(id);
    setSelectedSessionTimeRangeStatus(status);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedSessionTimeRangeId(null);
    setSelectedSessionTimeRangeStatus(null);
  };

  const openSessionTimeRangeDetailsPage = (id) => {
    navigate(`/session-time-range_details/${id}`);
  };

  const handleAllSessionTimeRangesClick = () => {
    setShowAllSessionTimeRanges(!showAllSessionTimeRanges);
    loadSessionTimeRanges();
  };

  const openCreateDialog = () => setIsDialogOpen(true);

  const columns = [
    { field: "sessionTime", headerName: "Session Time", flex: 1, minWidth: 150 },
    { field: "timeDescription", headerName: "Time Description", flex: 1, minWidth: 200 },
    { field: "status", headerName: "Status", flex: 1, minWidth: 100 },
    { field: "createdAt", headerName: "Created At", flex: 1, minWidth: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center">
          <IconButton color="info" onClick={() => handleEditClick(row.id)}>
            <EditOutlined />
          </IconButton>
          <IconButton color="secondary" onClick={() => handleDeleteClick(row.id, row.status)}>
            <Delete style={{ color: "red" }} />
          </IconButton>
          <IconButton color="success" onClick={() => openSessionTimeRangeDetailsPage(row.id)}>
            <VisibilityOutlinedIcon style={{ color: "green" }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box m="0px">
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        sessionTimeRangeId={selectedSessionTimeRangeId}
        currentStatus={selectedSessionTimeRangeStatus}
        onSuccess={loadSessionTimeRanges}
      />

      <CreateDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={loadSessionTimeRanges}
      />

      <EditSessionTimeRangeDialog
        sessionTimeRangeId={selectedIdForEdit}
        open={isEditDialogOpen}
        onClose={closeEditDialog}
        onSuccess={loadSessionTimeRanges}
      />

      <Box p={2} ml={2} mr={2}>
        <Header title="SESSION TIME RANGES" />
        <Box>
          <Button
            color="secondary"
            variant="contained"
            style={{ padding: "5px 8px", fontSize: "13px", marginRight: "10px" }}
            onClick={handleAllSessionTimeRangesClick}
          >
            {showAllSessionTimeRanges ? "ACTIVE ONLY" : "ALL SESSION TIME RANGES"}
          </Button>
          <Button
            color="secondary"
            variant="contained"
            style={{ padding: "5px 8px", fontSize: "13px" }}
            onClick={openCreateDialog}
          >
            ADD SESSION TIME
          </Button>
        </Box>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <Table
            rows={sessionTimeRanges}
            columns={columns}
            totalRecords={totalRecords}
            size={size}
            page={page}
            handlePageChange={handlePageChange}
            colors={colors}
          />
        )}
      </Box>
    </Box>
  );
};

export default SessionTimeRanges;

